import { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Container,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Paper,
  Divider,
  CircularProgress,
  SelectChangeEvent,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  ArrowBack,
  Add,
  PhotoCamera,
  Save,
  Cancel,
  MedicalServices,
  ExpandMore,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { servicesService } from './services.service';
import { DEFAULT_CURRENCY } from '../../constants';
import JoditEditor from '../../components/JoditEditor';

import { useToast } from '../../contexts/ToastContext';
import {
  extractApiErrorMessage,
  mapApiErrorToField,
} from '../../utils/apiErrorMapping';
const AddService = () => {
  const { showError } = useToast();
  const navigate = useNavigate();
  const { token, logout } = useAuth();
  const [formData, setFormData] = useState({
    about_examination: '',
    preparation_instructions: '',
    directions:
      'St. Michael’s Hospital</br>36 Queen Street East,</br>Toronto, Ontario, M5B 1W8</br></br>•	Enter through the Queen Street (Donnelly Wing) entrance located off Queen Street East, mid-way between Victoria and Bond Streets.</br>•	Just past the Information Desk, take the ramp on your left into the Peter Gilgan Tower (PGT)</br>•	Take the elevators to the third floor</br>•	Exit right out of the elevators and go down the hall to the end of the glass atrium</br>•	At the end of the hall, turn left, and follow the purple ceiling-mounted signs to PET/CT</br>',
    ground_level: '',
    parking:
      'Green-P Parking (Open 24 hours)</br>33 Queen St E, Toronto, ON M5C 1R5</br>Located across Queen Street from St. Michael’s Hospital, Peter Gilgan Tower entrance</br>www.parking.greenp.com',
    rates:
      '$4.75 per each half hour</br>Mon-Fri daytime maximum with entry between 7 am – 10 am is $32</br>Mon-Fri daytime maximum with entry between 10 am – 6 pm is $20</br>Sat-Sun daytime maximum with entry between 7 am – 6 pm is $11',
    how_to_pay:
      'Take a ticket and pay by cash or card, or</br>Scan your Green P app QR code',
    drop_off:
      'St. Michael’s Hospital, Queen Street entrance</br>36 Queen Street East, Toronto, Ontario, M5B 1W8',
    ttc: 'Queen Station</br>Exit at south end of the platform on the east side of Yonge Street </br>Go to the corner of Queen Street East and Victoria Street',
    sortOrder: 0,
    title: '',
    description: '',
    shortDescription: '',
    price: '',
    discount: '0',
    status: 1, // 1 = Active, 0 = Inactive
    duration: '',
    preparation: '',
    image: '',
    image_main: '',
    isAvailable: 1, // 1 = Available, 0 = Unavailable
    parent_id: 0, // 0 = Parent service, or specific parent service ID
  });
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string>('');
  const [imageGroundLevelFile, setImageGroundLevelFile] = useState<File | null>(
    null
  );
  const [imageMainFile, setImageMainFile] = useState<File | null>(null);
  const [imageMainPreview, setImageMainPreview] = useState<string>('');
  const [imageGroundLevelPreview, setImageGroundLevelPreview] =
    useState<string>('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [parentServices, setParentServices] = useState<
    Array<{ id: number; title: string }>
  >([]);
  const [loadingParents, setLoadingParents] = useState(false);
  // Each of the 8 rich-text fields below (about_examination through ttc)
  // is its own heavyweight Jodit WYSIWYG editor - mounting all 8 at once on
  // page load was the main reason this page felt slow. Collapsed behind an
  // Accordion by default, and the editor itself is only rendered once its
  // section is actually expanded, so the admin only pays the init cost for
  // the sections they open.
  const [expandedFields, setExpandedFields] = useState<Set<string>>(new Set());
  const toggleField =
    (field: string) => (_event: React.SyntheticEvent, isExpanded: boolean) => {
      setExpandedFields(prev => {
        const next = new Set(prev);
        if (isExpanded) {
          next.add(field);
        } else {
          next.delete(field);
        }
        return next;
      });
    };

  const statuses = [
    { value: 1, label: 'Active' },
    { value: 0, label: 'Inactive' },
  ];
  const isAvailables = [
    { value: 1, label: 'Available' },
    { value: 0, label: 'Unavailable' },
  ];

  // Fetch parent services on component mount
  useEffect(() => {
    // Set up logout callback for session expired
    servicesService.setLogoutCallback(logout);

    const fetchParentServices = async () => {
      if (!token) return;

      setLoadingParents(true);
      try {
        const services = await servicesService.getParentServices(token);
        console.log('Fetched parent services:', services);
        setParentServices(services);
      } catch (error) {
        console.error('Error fetching parent services:', error);
        showError('Failed to load parent services');
      } finally {
        setLoadingParents(false);
      }
    };

    fetchParentServices();
  }, [token, logout]);

  const handleChange =
    (field: keyof typeof formData) =>
    (
      event:
        | React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
        | SelectChangeEvent
    ) => {
      let value: string | number = (event.target as HTMLInputElement).value;
      console.log('field', field, 'value', value);
      if (
        field === 'status' ||
        field === 'isAvailable' ||
        field === 'parent_id' ||
        field === 'sortOrder'
      ) {
        value = Number(value);
      }
      setFormData({ ...formData, [field]: value });
      if (errors[field as string]) {
        setErrors({ ...errors, [field as string]: '' });
      }
    };

  const handleImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        showError('Image size should be less than 5MB');
        return;
      }

      // Validate file type
      if (!file.type.startsWith('image/')) {
        showError('Please upload a valid image file');
        return;
      }

      setImageFile(file);

      // Create preview URL
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);

      // Clear image error if exists
      if (errors.image) {
        setErrors({ ...errors, image: '' });
      }
    }
  };

  const handleRemoveImage = () => {
    setImageFile(null);
    setImagePreview('');
    setFormData({ ...formData, image: '' });
  };

  const handleImageMainChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        showError('Image size should be less than 5MB');
        return;
      }

      // Validate file type
      if (!file.type.startsWith('image/')) {
        showError('Please upload a valid image file');
        return;
      }

      setImageMainFile(file);

      // Create preview URL
      const reader = new FileReader();
      reader.onloadend = () => {
        setImageMainPreview(reader.result as string);
      };
      reader.readAsDataURL(file);

      // Clear image error if exists
      if (errors.image_main) {
        setErrors({ ...errors, image_main: '' });
      }
    }
  };

  const handleRemoveImageMain = () => {
    setImageMainFile(null);
    setImageMainPreview('');
    setFormData({ ...formData, image_main: '' });
  };

  const handleImageGroundLevelChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        showError('Image size should be less than 5MB');
        return;
      }

      // Validate file type
      if (!file.type.startsWith('image/')) {
        showError('Please upload a valid image file');
        return;
      }

      setImageGroundLevelFile(file);

      // Create preview URL
      const reader = new FileReader();
      reader.onloadend = () => {
        setImageGroundLevelPreview(reader.result as string);
      };
      reader.readAsDataURL(file);

      // Clear image error if exists
      if (errors.ground_level) {
        setErrors({ ...errors, ground_level: '' });
      }
    }
  };

  const handleRemoveImageGroundLevel = () => {
    setImageGroundLevelFile(null);
    setImageGroundLevelPreview('');
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.title.trim()) {
      newErrors.title = 'Service title is required';
    }

    if (!formData.description.trim()) {
      newErrors.description = 'Description is required';
    }

    if (!formData.shortDescription.trim()) {
      newErrors.shortDescription = 'Short description is required';
    } else if (
      formData.shortDescription.trim().length < 130 ||
      formData.shortDescription.trim().length > 150
    ) {
      // maxLength=150 on the field only stops typing past 150 (and
      // silently truncates a paste with no feedback) - it never enforced
      // the 130 minimum the helper text also promises.
      newErrors.shortDescription = `Short description must be 130-150 characters (currently ${formData.shortDescription.trim().length})`;
    }

    if (!formData.price || parseFloat(formData.price) <= 0) {
      newErrors.price = 'Valid price is required';
    }

    if (!formData.duration.trim()) {
      newErrors.duration = 'Duration is required';
    } else if (
      !Number.isFinite(Number(formData.duration)) ||
      Number(formData.duration) <= 0
    ) {
      // '0' and '00' both pass a plain "is it empty" check but aren't a
      // real duration - a scan can't take 0 minutes.
      newErrors.duration = 'Duration must be greater than 0 minutes';
    }

    if (!imageFile && !formData.image) {
      newErrors.image = 'Service image is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      showError('Please fix all validation errors before submitting');
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    if (!token) return;

    setLoading(true);
    try {
      // Create FormData for file upload
      const submitFormData = new FormData();
      submitFormData.append('title', formData.title);
      submitFormData.append('description', formData.description);
      submitFormData.append('shortDescription', formData.shortDescription);
      submitFormData.append('price', formData.price);
      submitFormData.append('discount', formData.discount);
      submitFormData.append('status', String(formData.status));
      submitFormData.append('duration', formData.duration);
      submitFormData.append('preparation', formData.preparation || '');
      submitFormData.append('parent_id', String(formData.parent_id));
      submitFormData.append('sortOrder', String(formData.sortOrder));
      submitFormData.append('isAvailable', String(formData.isAvailable));

      submitFormData.append('about_examination', formData.about_examination);
      submitFormData.append(
        'preparation_instructions',
        formData.preparation_instructions
      );
      submitFormData.append('directions', formData.directions);
      submitFormData.append('ground_level', formData.ground_level);
      submitFormData.append('parking', formData.parking);
      submitFormData.append('rates', formData.rates);
      submitFormData.append('how_to_pay', formData.how_to_pay);
      submitFormData.append('drop_off', formData.drop_off);
      submitFormData.append('ttc', formData.ttc);

      // Append image file if exists
      if (imageFile) {
        submitFormData.append('image', imageFile);
      }

      // Append image_main file if exists
      if (imageMainFile) {
        submitFormData.append('image_main', imageMainFile);
      }

      // Append image_main file if exists
      if (imageGroundLevelFile) {
        submitFormData.append('ground_level', imageGroundLevelFile);
      }

      console.log('Submitting FormData with image:', imageFile?.name);
      console.log('Submitting FormData with image_main:', imageMainFile?.name);
      console.log('FormData contents:');
      for (const [key, value] of submitFormData.entries()) {
        console.log(key, value);
      }

      await servicesService.createServiceWithImage(token, submitFormData);

      // Was: showSuccess() here immediately, *then* navigate 1.5s later
      // with a location.state.message that made the listing page show its
      // own toast too - the same success popped up 2-3 times (worse in
      // dev, where StrictMode double-invokes the listing page's effect).
      // One source of truth: only the listing page shows it, right when
      // the user actually lands there.
      navigate('/services', {
        state: {
          message: 'Service created successfully',
          severity: 'success',
        },
      });
    } catch (err) {
      console.error('Error creating service:', err);
      const message = extractApiErrorMessage(err) || 'Failed to create service';
      const { field } = mapApiErrorToField(message, Object.keys(formData));
      if (field) {
        // Show it inline under the field it's actually about, same as
        // client-side validation - not just a toast the user can't map
        // to an input.
        setErrors(prev => ({ ...prev, [field]: message }));
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
      showError(message);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    // Return to wherever the listing was left (search term, page number)
    // rather than a bare /services, which always reset it - same fix as
    // the header back button below.
    navigate(-1);
  };

  return (
    <Container maxWidth="lg">
      <Box sx={{ py: 4 }}>
        {/* Header */}
        <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
          <Box display="flex" alignItems="center" mb={2}>
            <IconButton onClick={() => navigate(-1)} sx={{ mr: 2 }}>
              <ArrowBack />
            </IconButton>
            <Add color="primary" sx={{ mr: 1 }} />
            <Typography variant="h4" component="h1">
              Add New Service
            </Typography>
          </Box>
          <Typography variant="body1" color="text.secondary">
            Create a new medical service with detailed information
          </Typography>
        </Paper>

        <Grid container spacing={3}>
          {/* Service Image Section */}
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Service Image
                </Typography>
                <Box
                  sx={{
                    border: '2px dashed',
                    borderColor: imagePreview ? 'primary.main' : 'grey.300',
                    borderRadius: 2,
                    p: imagePreview ? 0 : 4,
                    textAlign: 'center',
                    mb: 2,
                    minHeight: 200,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    overflow: 'hidden',
                    position: 'relative',
                  }}
                >
                  {imagePreview ? (
                    <>
                      <Box
                        component="img"
                        src={imagePreview}
                        alt="Service preview"
                        sx={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                        }}
                      />
                      <IconButton
                        onClick={handleRemoveImage}
                        sx={{
                          position: 'absolute',
                          top: 8,
                          right: 8,
                          bgcolor: 'background.paper',
                          '&:hover': {
                            bgcolor: 'error.main',
                            color: 'white',
                          },
                        }}
                      >
                        <Cancel />
                      </IconButton>
                    </>
                  ) : (
                    <Box>
                      <MedicalServices
                        sx={{ fontSize: 48, color: 'grey.400', mb: 1 }}
                      />
                      <Typography variant="body2" color="text.secondary">
                        Upload service image
                      </Typography>
                    </Box>
                  )}
                </Box>
                <Button
                  variant="outlined"
                  startIcon={<PhotoCamera />}
                  component="label"
                  fullWidth
                >
                  {imagePreview ? 'Change Image' : 'Upload Image'}
                  <input
                    hidden
                    accept="image/*"
                    type="file"
                    onChange={handleImageChange}
                  />
                </Button>
                {errors.image && (
                  <Typography
                    variant="caption"
                    color="error"
                    sx={{ mt: 1, display: 'block' }}
                  >
                    {errors.image}
                  </Typography>
                )}
                <Typography
                  variant="body2"
                  color="error"
                  sx={{ fontWeight: 500, lineHeight: 1.6 }}
                >
                  Image should be <strong>404*302px</strong>. Use JPG or PNG
                  format under 500 KB before upload.
                </Typography>
              </CardContent>
            </Card>

            {/* Main Image Section */}
            <Card sx={{ mt: 2 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Main Service Image
                </Typography>
                <Box
                  sx={{
                    border: '2px dashed',
                    borderColor: imageMainPreview ? 'primary.main' : 'grey.300',
                    borderRadius: 2,
                    p: imageMainPreview ? 0 : 4,
                    textAlign: 'center',
                    mb: 2,
                    minHeight: 200,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    overflow: 'hidden',
                    position: 'relative',
                  }}
                >
                  {imageMainPreview ? (
                    <>
                      <Box
                        component="img"
                        src={imageMainPreview}
                        alt="Main service preview"
                        sx={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                        }}
                      />
                      <IconButton
                        onClick={handleRemoveImageMain}
                        sx={{
                          position: 'absolute',
                          top: 8,
                          right: 8,
                          bgcolor: 'background.paper',
                          '&:hover': {
                            bgcolor: 'error.main',
                            color: 'white',
                          },
                        }}
                      >
                        <Cancel />
                      </IconButton>
                    </>
                  ) : (
                    <Box>
                      <MedicalServices
                        sx={{ fontSize: 48, color: 'grey.400', mb: 1 }}
                      />
                      <Typography variant="body2" color="text.secondary">
                        Upload main service image
                      </Typography>
                    </Box>
                  )}
                </Box>
                <Button
                  variant="outlined"
                  startIcon={<PhotoCamera />}
                  component="label"
                  fullWidth
                >
                  {imageMainPreview ? 'Change Main Image' : 'Upload Main Image'}
                  <input
                    hidden
                    accept="image/*"
                    type="file"
                    onChange={handleImageMainChange}
                  />
                </Button>
                {errors.image_main && (
                  <Typography
                    variant="caption"
                    color="error"
                    sx={{ mt: 1, display: 'block' }}
                  >
                    {errors.image_main}
                  </Typography>
                )}
                <Typography
                  variant="body2"
                  color="error"
                  sx={{ fontWeight: 500, lineHeight: 1.6 }}
                >
                  Image should be <strong>717*717px</strong>. Use JPG or PNG
                  format under 500 KB before upload.
                </Typography>
              </CardContent>
            </Card>

            {/* Ground Level Image Section */}
            <Card sx={{ mt: 2 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Ground Level Image
                </Typography>
                <Box
                  sx={{
                    border: '2px dashed',
                    borderColor: imageGroundLevelPreview
                      ? 'primary.main'
                      : 'grey.300',
                    borderRadius: 2,
                    p: imageGroundLevelPreview ? 0 : 4,
                    textAlign: 'center',
                    mb: 2,
                    minHeight: 200,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    overflow: 'hidden',
                    position: 'relative',
                  }}
                >
                  {imageGroundLevelPreview ? (
                    <>
                      <Box
                        component="img"
                        src={imageGroundLevelPreview}
                        alt="Main service preview"
                        sx={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                        }}
                      />
                      <IconButton
                        onClick={handleRemoveImageGroundLevel}
                        sx={{
                          position: 'absolute',
                          top: 8,
                          right: 8,
                          bgcolor: 'background.paper',
                          '&:hover': {
                            bgcolor: 'error.main',
                            color: 'white',
                          },
                        }}
                      >
                        <Cancel />
                      </IconButton>
                    </>
                  ) : (
                    <Box>
                      <MedicalServices
                        sx={{ fontSize: 48, color: 'grey.400', mb: 1 }}
                      />
                      <Typography variant="body2" color="text.secondary">
                        Upload ground level image
                      </Typography>
                    </Box>
                  )}
                </Box>
                <Button
                  variant="outlined"
                  startIcon={<PhotoCamera />}
                  component="label"
                  fullWidth
                >
                  {imageGroundLevelPreview
                    ? 'Change Ground Level Image'
                    : 'Upload Ground Level Image'}
                  <input
                    hidden
                    accept="image/*"
                    type="file"
                    onChange={handleImageGroundLevelChange}
                  />
                </Button>
                {errors.ground_level && (
                  <Typography
                    variant="caption"
                    color="error"
                    sx={{ mt: 1, display: 'block' }}
                  >
                    {errors.ground_level}
                  </Typography>
                )}
                <Typography
                  variant="body2"
                  color="error"
                  sx={{ fontWeight: 500, lineHeight: 1.6 }}
                >
                  {/* Image should be <strong>717*717px</strong>. Use JPG or PNG
                  format under 500 KB before upload. */}
                </Typography>
              </CardContent>
            </Card>

            {/* Service Settings */}
            <Card sx={{ mt: 2 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Service Settings
                </Typography>
                <FormControl fullWidth margin="normal">
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={String(formData.status)}
                    label="Status"
                    onChange={handleChange('status')}
                  >
                    {statuses.map(status => (
                      <MenuItem key={status.value} value={String(status.value)}>
                        {status.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl fullWidth margin="normal">
                  <InputLabel>Is Available</InputLabel>
                  <Select
                    value={String(formData.isAvailable)}
                    label="Is Available"
                    onChange={handleChange('isAvailable')}
                  >
                    {isAvailables.map(status => (
                      <MenuItem key={status.value} value={String(status.value)}>
                        {status.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </CardContent>
            </Card>
          </Grid>

          {/* Form Section */}
          <Grid item xs={12} md={8}>
            <Card>
              <CardContent>
                <Box component="form" onSubmit={handleSubmit} noValidate>
                  <Grid container spacing={3}>
                    {/* Parent Service Selection */}
                    <Grid item xs={12}>
                      <Typography variant="h6" gutterBottom>
                        Service Hierarchy
                      </Typography>
                    </Grid>

                    <Grid item xs={12}>
                      <FormControl fullWidth>
                        <InputLabel>Parent Service</InputLabel>
                        <Select
                          value={String(formData.parent_id)}
                          label="Parent Service"
                          onChange={handleChange('parent_id')}
                          disabled={loadingParents}
                        >
                          <MenuItem value={0}>
                            <em>Parent</em>
                          </MenuItem>
                          {parentServices.map(service => {
                            // console.log('Rendering service:', service);
                            return (
                              <MenuItem key={service.id} value={service.id}>
                                {service.title}
                              </MenuItem>
                            );
                          })}
                        </Select>
                      </FormControl>
                      {loadingParents && (
                        <Typography>Loading parent services...</Typography>
                      )}
                      <Typography variant="caption" color="textSecondary">
                        Found {parentServices.length} parent services
                      </Typography>
                    </Grid>

                    {/* Basic Information */}
                    <Grid item xs={12}>
                      <Typography variant="h6" gutterBottom>
                        Basic Information
                      </Typography>
                    </Grid>

                    <Grid item xs={12} sm={8}>
                      <TextField
                        fullWidth
                        label="Service Title"
                        value={formData.title}
                        onChange={handleChange('title')}
                        error={!!errors.title}
                        helperText={errors.title}
                        required
                        placeholder="e.g., Brain Scan"
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        fullWidth
                        label="Duration (minutes)"
                        type="number"
                        value={formData.duration}
                        onChange={handleChange('duration')}
                        error={!!errors.duration}
                        helperText={errors.duration}
                        required
                        placeholder="45"
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Description"
                        multiline
                        rows={4}
                        value={formData.description}
                        onChange={handleChange('description')}
                        error={!!errors.description}
                        helperText={errors.description}
                        required
                        placeholder="Description of the service..."
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Short Description"
                        multiline
                        rows={4}
                        value={formData.shortDescription}
                        onChange={handleChange('shortDescription')}
                        error={!!errors.shortDescription}
                        inputProps={{ maxLength: 150 }}
                        helperText={
                          errors.shortDescription ||
                          `Note: Only accept 130 - 150 characters only (${formData.shortDescription.length}/150)`
                        }
                        FormHelperTextProps={{
                          sx: {
                            color: 'error.main',
                            fontWeight: 500,
                          },
                        }}
                        required
                        placeholder="Short description of the service..."
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label={`Price (${DEFAULT_CURRENCY})`}
                        type="number"
                        value={formData.price}
                        onChange={handleChange('price')}
                        error={!!errors.price}
                        helperText={errors.price}
                        required
                        placeholder="450.00"
                        InputProps={{
                          startAdornment: (
                            <Typography sx={{ mr: 1 }}>
                              {DEFAULT_CURRENCY}
                            </Typography>
                          ),
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label={`Sort Order`}
                        type="number"
                        value={formData.sortOrder}
                        onChange={handleChange('sortOrder')}
                        error={!!errors.sortOrder}
                        helperText={errors.sortOrder}
                        placeholder="0"
                      />
                    </Grid>
                  </Grid>
                </Box>
              </CardContent>
            </Card>
            <Card sx={{ mt: 2 }}>
              <CardContent>
                <Box component="form" onSubmit={handleSubmit} noValidate>
                  <Grid container spacing={3}>
                    {/* Parent Service Selection */}
                    <Grid item xs={12}>
                      <Typography variant="h6" gutterBottom>
                        Preparation Instructions
                      </Typography>
                    </Grid>
                    {[
                      {
                        field: 'about_examination' as const,
                        label: 'About the Examination',
                        placeholder: 'About the Examination of the service...',
                      },
                      {
                        field: 'preparation_instructions' as const,
                        label: 'Preparation Instructions',
                        placeholder:
                          'Preparation Instructions of the service...',
                      },
                      {
                        field: 'directions' as const,
                        label: 'Directions',
                        placeholder: 'Directions of the service...',
                      },
                      {
                        field: 'parking' as const,
                        label: 'Parking',
                        placeholder: 'Parking of the service...',
                      },
                      {
                        field: 'rates' as const,
                        label: 'Rates',
                        placeholder: 'Rates of the service...',
                      },
                      {
                        field: 'how_to_pay' as const,
                        label: 'How to Pay',
                        placeholder: 'How to Pay of the service...',
                      },
                      {
                        field: 'drop_off' as const,
                        label: 'Drop Off',
                        placeholder: 'Drop Off of the service...',
                      },
                      {
                        field: 'ttc' as const,
                        label: 'TTC',
                        placeholder: 'TTC of the service...',
                      },
                    ].map(({ field, label, placeholder }) => (
                      <Grid item xs={12} key={field}>
                        <Accordion
                          expanded={expandedFields.has(field)}
                          onChange={toggleField(field)}
                        >
                          <AccordionSummary expandIcon={<ExpandMore />}>
                            <Typography
                              sx={{
                                color: errors[field]
                                  ? '#d32f2f'
                                  : 'text.primary',
                                fontWeight: errors[field] ? 600 : 400,
                              }}
                            >
                              {label}
                            </Typography>
                          </AccordionSummary>
                          <AccordionDetails>
                            {expandedFields.has(field) && (
                              <JoditEditor
                                value={formData[field]}
                                onChange={value =>
                                  setFormData({ ...formData, [field]: value })
                                }
                                placeholder={placeholder}
                                error={!!errors[field]}
                                height={250}
                              />
                            )}
                            {errors[field] && (
                              <Typography
                                variant="caption"
                                sx={{ color: '#d32f2f', mt: 0.5, ml: 1.75 }}
                              >
                                {errors[field]}
                              </Typography>
                            )}
                          </AccordionDetails>
                        </Accordion>
                      </Grid>
                    ))}
                  </Grid>

                  <Divider sx={{ my: 3 }} />
                  <Box display="flex" justifyContent="flex-end" gap={2}>
                    <Button
                      variant="outlined"
                      onClick={handleCancel}
                      startIcon={<Cancel />}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      variant="contained"
                      startIcon={
                        loading ? <CircularProgress size={20} /> : <Save />
                      }
                      disabled={loading}
                      sx={{
                        background:
                          'linear-gradient(45deg, #1976d2 30%, #42a5f5 90%)',
                        '&:hover': {
                          background:
                            'linear-gradient(45deg, #1565c0 30%, #1976d2 90%)',
                        },
                      }}
                    >
                      {loading ? 'Creating...' : 'Create Service'}
                    </Button>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
    </Container>
  );
};

export default AddService;
